import { useEffect, useRef } from 'react';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { useImageGenerationStore } from '../stores/imageGenerationStore';
import { useCoinStore } from '../stores/coinStore';
import { MessageType, TaskType } from '../services/websocket/types';
import { imageGenerationApi } from '../services/user/imageGeneration.api';
import { ImageGenerationStatus, TaskType as GlobalTaskType } from '@packages/global';

/**
 * useImageGenerationWebSocket Hook
 * 
 * 订阅图片生成相关的 WebSocket 事件，实时更新状态
 */
export function useImageGenerationWebSocket() {
  const { client, isConnected } = useWebSocketContext();
  const updateTask = useImageGenerationStore(state => state.updateTask);
  const updateBalance = useCoinStore(state => state.updateBalance);

  // 用 ref 稳定回调，避免 Zustand selector 引用变化导致 effect 重跑
  const updateTaskRef = useRef(updateTask);
  updateTaskRef.current = updateTask;
  const updateBalanceRef = useRef(updateBalance);
  updateBalanceRef.current = updateBalance;

  useEffect(() => {
    if (!client || !isConnected) {
      return;
    }

    // 订阅任务状态变化事件（标准 WebSocket 事件）
    const unsubscribeStatusChanged = client.onMessage(
      MessageType.TASK_STATUS_CHANGED,
      async (message: any) => {
        // 处理图片生成和图片编辑任务（兼容大小写和下划线格式）
        const taskType = message.taskType?.toUpperCase().replace(/-/g, '_');
        const isImageTask =
          taskType === GlobalTaskType.IMAGE_GENERATION.toUpperCase().replace(/-/g, '_') ||
          taskType === GlobalTaskType.IMAGE_EDIT.toUpperCase().replace(/-/g, '_');

        if (isImageTask && message.taskId) {
          const statusMap: Record<string, ImageGenerationStatus> = {
            'pending': ImageGenerationStatus.PENDING,
            'submitted': ImageGenerationStatus.SUBMITTED,
            'processing': ImageGenerationStatus.PROCESSING,
            'completed': ImageGenerationStatus.COMPLETED,
            'failed': ImageGenerationStatus.FAILED
          };

          const mappedStatus = statusMap[message.newStatus?.toLowerCase()] || message.newStatus;

          if (mappedStatus === ImageGenerationStatus.COMPLETED) {
            try {
              const fullTask = await imageGenerationApi.getImageGeneration(message.taskId);
              updateTaskRef.current(message.taskId, fullTask);
            } catch (error) {
              console.error('[useImageGenerationWebSocket] Failed to fetch full task:', error);
              updateTaskRef.current(message.taskId, { status: mappedStatus });
            }
          } else {
            updateTaskRef.current(message.taskId, { status: mappedStatus });
          }
        }
      }
    );

    // 订阅任务完成事件
    const unsubscribeCompleted = client.onMessage(
      MessageType.TASK_COMPLETED,
      (message: any) => {
        const taskType = message.taskType?.toUpperCase().replace(/-/g, '_');
        const isImageTask =
          taskType === GlobalTaskType.IMAGE_GENERATION.toUpperCase().replace(/-/g, '_') ||
          taskType === GlobalTaskType.IMAGE_EDIT.toUpperCase().replace(/-/g, '_');

        if (isImageTask && message.taskId) {
          updateTaskRef.current(message.taskId, {
            status: ImageGenerationStatus.COMPLETED,
            resultImageUrl: message.data?.imageUrl,
            resultMessage: '图片生成成功'
          });
        }
      }
    );

    // 订阅任务失败事件
    const unsubscribeFailed = client.onMessage(
      MessageType.TASK_FAILED,
      (message: any) => {
        const taskType = message.taskType?.toUpperCase().replace(/-/g, '_');
        const isImageTask =
          taskType === GlobalTaskType.IMAGE_GENERATION.toUpperCase().replace(/-/g, '_') ||
          taskType === GlobalTaskType.IMAGE_EDIT.toUpperCase().replace(/-/g, '_');

        if (isImageTask && message.taskId) {
          updateTaskRef.current(message.taskId, {
            status: ImageGenerationStatus.FAILED,
            resultMessage: message.error || '图片生成失败'
          });
        }
      }
    );

    // 订阅剧点余额更新事件
    const unsubscribeCoinBalance = client.onMessage(
      MessageType.COIN_BALANCE_CHANGED,
      (message: any) => {
        if (message.balance !== undefined) {
          updateBalanceRef.current(message.balance);
        }
      }
    );

    return () => {
      unsubscribeStatusChanged();
      unsubscribeCompleted();
      unsubscribeFailed();
      unsubscribeCoinBalance();
    };
  }, [client, isConnected]); // 移除 updateTask/updateBalance，通过 ref 访问避免重复订阅
}
